/**
 * Long Table floor-length check: warns when a Long Table Feast layout's
 * rectangle-table row arrangement would not realistically fit the room's
 * available linear floor length. PRD §8.2's "Long Table floor-length check"
 * row.
 */

import type { LayoutObject, LayoutStyle, RoomDefinition, ValidationIssue } from '../../types';

const RECT_TABLE_LENGTH_FT = 8;
const MIN_TABLE_GAP_FT = 1;
const USABLE_WIDTH_MARGIN_FT = 4; // matches the ~2ft-per-side margin used by the placement engine

export function checkLongTableFloorLength(
  objects: LayoutObject[],
  room: RoomDefinition,
  layoutStyle: LayoutStyle
): ValidationIssue[] {
  if (layoutStyle !== 'long_table_feast') return [];

  const rectTables = objects.filter((o) => o.type === 'rect_table_8');
  if (rectTables.length === 0) return [];

  const usableWidth = Math.max(room.widthFt, room.lengthFt) - USABLE_WIDTH_MARGIN_FT;
  const tablesPerRow = Math.max(1, Math.floor(usableWidth / (RECT_TABLE_LENGTH_FT + MIN_TABLE_GAP_FT)));
  const rowsNeeded = Math.ceil(rectTables.length / tablesPerRow);
  const usableDepth = Math.min(room.widthFt, room.lengthFt) - USABLE_WIDTH_MARGIN_FT;
  const depthNeeded = rowsNeeded * 5.5; // matches RECT_ROW_GAP_FT + RECT_TABLE_DEPTH_FT from patterns.ts

  if (depthNeeded > usableDepth) {
    return [
      {
        id: 'long-table-floor-length',
        // Informational, not Warning — this is a forward-looking
        // recommendation ("may not fit comfortably... consider...")
        // about the chosen STYLE for this guest count and room, not a
        // description of an actual problem with the layout as it
        // currently exists. A coordinator who's already aware of this
        // tradeoff and proceeding anyway shouldn't see it nagging at
        // the same level as a real spatial conflict.
        severity: 'info',
        message:
          'This guest count may not fit comfortably as Long Table Feast in this room. Consider Classic Banquet or a larger room.',
        relatedObjectIds: rectTables.map((t) => t.id),
      },
    ];
  }

  return [];
}
